// Create a new file: components/ui/career-survey-modal.jsx
import React, { useState, useEffect } from "react";
import { X, ChevronRight, Target, Search, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const CareerSurveyModal = ({ isOpen, onClose, themeColors }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [careerInput, setCareerInput] = useState("");
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [animateIn, setAnimateIn] = useState(false);
  const navigate = useNavigate();

  const careerOptions = [
    { id: 1, title: "Software Engineering", icon: "💻", description: "Build applications and systems" },
    { id: 2, title: "Data Science", icon: "📊", description: "Analyze and interpret complex data" },
    { id: 3, title: "UX/UI Design", icon: "🎨", description: "Create intuitive user experiences" },
    { id: 4, title: "Digital Marketing", icon: "📈", description: "Promote brands online" },
    { id: 5, title: "Cybersecurity", icon: "🔒", description: "Protect digital assets" },
    { id: 6, title: "Business Analytics", icon: "📋", description: "Drive business decisions with data" },
    { id: 7, title: "Product Management", icon: "🎯", description: "Lead product development" },
    { id: 8, title: "Artificial Intelligence", icon: "🤖", description: "Develop intelligent systems" },
  ];

  useEffect(() => {
    if (isOpen) {
      // Small delay for animation
      setTimeout(() => setAnimateIn(true), 50);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (currentStep === 1 && selectedOption === "yes") {
      // Store the career path in localStorage or send to backend
      localStorage.setItem("userCareerPath", careerInput);
      navigate("/career-learning-path");
      onClose();
    } else if (currentStep === 2 && selectedCareer) {
      // Store selected career
      localStorage.setItem("userCareerPath", selectedCareer.title);
      navigate("/curriculum");
      onClose();
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    if (option === "yes") {
      setCurrentStep(1.5); // Show input step
    } else if (option === "no") {
      setCurrentStep(2); // Show career selection
    }
  };

  const handleBack = () => {
    if (currentStep === 1.5) {
      setCurrentStep(1);
      setSelectedOption(null);
    } else if (currentStep === 2) {
      setCurrentStep(1);
      setSelectedOption(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Blur backdrop only */}
      <div 
        className={`fixed inset-0 z-50 backdrop-blur-sm transition-opacity duration-500 ${
          animateIn ? "opacity-100" : "opacity-0"
        }`}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}
        onClick={(e) => {
          if (e.target === e.currentTarget && currentStep === 1) {
            onClose();
          }
        }}
      />
      
      {/* Modal container - NOT blurry */}
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-500 pointer-events-none ${
          animateIn ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}
      >
        {/* Modal content - NOT blurry */}
        <div 
          className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden pointer-events-auto"
          style={{ 
            backgroundColor: themeColors.cardBackground,
            border: `1px solid ${themeColors.border}`
          }}
        >
          {/* Close button - only visible on first step */}
          {currentStep === 1 && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full transition-all hover:scale-110 hover:bg-white/20"
              style={{ 
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: themeColors.foreground
              }}
            >
              <X size={20} />
            </button>
          )}

          {/* Modal header */}
          <div 
            className="p-8 text-center relative overflow-hidden"
            style={{ backgroundColor: themeColors.primary + "10" }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
              style={{ backgroundColor: themeColors.primary }}
            />
            <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-5"
              style={{ backgroundColor: themeColors.primary }}
            />

            <div className="relative z-10">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: themeColors.primary + "20" }}
              >
                <Target size={28} style={{ color: themeColors.primary }} />
              </div>
              
              <h2 className="text-2xl font-bold mb-2"
                style={{ color: themeColors.foreground }}
              >
                {currentStep === 1 && "Career Path Discovery"}
                {currentStep === 1.5 && "Tell us about your path"}
                {currentStep === 2 && "Explore Career Options"}
              </h2>
              
              <p className="text-sm opacity-70"
                style={{ color: themeColors.foreground }}
              >
                {currentStep === 1 && "Help us personalize your learning journey"}
                {currentStep === 1.5 && "Enter your desired career path"}
                {currentStep === 2 && "Select a career that interests you"}
              </p>
            </div>
          </div>

          {/* Modal body */}
          <div className="p-8">
            {/* Step 1: Initial question */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <p className="text-lg text-center font-medium"
                    style={{ color: themeColors.foreground }}
                  >
                    Do you already have a career path in mind?
                  </p>
                  
                  <div className="grid gap-3">
                    <button
                      onClick={() => handleOptionSelect("yes")}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-between group ${
                        selectedOption === "yes" ? "border-primary" : "border-transparent"
                      }`}
                      style={{ 
                        backgroundColor: selectedOption === "yes" 
                          ? themeColors.primary + "15" 
                          : themeColors.cardBackground + "80",
                        borderColor: selectedOption === "yes" ? themeColors.primary : "transparent",
                        color: themeColors.foreground
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: themeColors.primary + "20" }}
                        >
                          <span className="text-lg">🎯</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Yes, I have a path</p>
                          <p className="text-xs opacity-70">I know what I want to pursue</p>
                        </div>
                      </div>
                      <ChevronRight size={20} className="opacity-50 group-hover:translate-x-1 transition-transform" />
                    </button>
                    
                    <button
                      onClick={() => handleOptionSelect("no")}
                      className={`p-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-between group ${
                        selectedOption === "no" ? "border-primary" : "border-transparent"
                      }`}
                      style={{ 
                        backgroundColor: selectedOption === "no" 
                          ? themeColors.primary + "15" 
                          : themeColors.cardBackground + "80",
                        borderColor: selectedOption === "no" ? themeColors.primary : "transparent",
                        color: themeColors.foreground
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: themeColors.primary + "20" }}
                        >
                          <span className="text-lg">🔍</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">No, I'm exploring</p>
                          <p className="text-xs opacity-70">Help me discover options</p>
                        </div>
                      </div>
                      <ChevronRight size={20} className="opacity-50 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1.5: Career input */}
            {currentStep === 1.5 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: themeColors.foreground }}
                    >
                      <ChevronRight size={16} className="rotate-180" />
                      Back
                    </button>
                    <span className="text-xs px-3 py-1 rounded-full"
                      style={{ 
                        backgroundColor: themeColors.primary + "20",
                        color: themeColors.primary
                      }}
                    >
                      Step 1 of 2
                    </span>
                  </div>
                  
                  <p className="text-lg font-medium mb-4"
                    style={{ color: themeColors.foreground }}
                  >
                    What career path are you pursuing?
                  </p>
                  
                  <div className="relative">
                    <Search 
                      size={20} 
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 opacity-50"
                      style={{ color: themeColors.foreground }}
                    />
                    <input
                      type="text"
                      value={careerInput}
                      onChange={(e) => setCareerInput(e.target.value)}
                      placeholder="e.g., Frontend Developer, Data Analyst, Product Designer..."
                      className="w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-1"
                      style={{ 
                        backgroundColor: "transparent",
                        borderColor: themeColors.border,
                        color: themeColors.foreground,
                        focusRingColor: themeColors.primary
                      }}
                      onKeyPress={(e) => e.key === "Enter" && handleSubmit()}
                    />
                  </div>
                  
                  <div className="flex gap-2 flex-wrap mt-4">
                    {["Software Engineer", "Data Scientist", "UX Designer", "Digital Marketer"].map((tag) => (
                      <button
                        key={tag}
                        onClick={() => setCareerInput(tag)}
                        className="px-3 py-1.5 text-sm rounded-lg transition-all hover:scale-105"
                        style={{ 
                          backgroundColor: themeColors.primary + "15",
                          color: themeColors.primary
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={handleSubmit}
                  disabled={!careerInput.trim()}
                  className={`w-full py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    careerInput.trim() ? "hover:scale-[1.02]" : "opacity-50 cursor-not-allowed"
                  }`}
                  style={{ 
                    backgroundColor: careerInput.trim() ? themeColors.primary : themeColors.primary + "50",
                    color: themeColors.cardBackground
                  }}
                >
                  Continue to Learning Path
                  <Sparkles size={18} />
                </button>
              </div>
            )}

            {/* Step 2: Career selection */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
                      style={{ color: themeColors.foreground }}
                    >
                      <ChevronRight size={16} className="rotate-180" />
                      Back
                    </button>
                    <span className="text-xs px-3 py-1 rounded-full"
                      style={{ 
                        backgroundColor: themeColors.primary + "20",
                        color: themeColors.primary
                      }}
                    >
                      Step 2 of 2
                    </span>
                  </div>
                  
                  <p className="text-lg font-medium mb-4"
                    style={{ color: themeColors.foreground }}
                  >
                    Which career interests you?
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
                    {careerOptions.map((career) => (
                      <button
                        key={career.id}
                        onClick={() => setSelectedCareer(career)}
                        className={`p-4 rounded-xl border-2 transition-all duration-300 text-left group ${
                          selectedCareer?.id === career.id ? "scale-[1.02]" : "hover:scale-[1.01]"
                        }`}
                        style={{ 
                          backgroundColor: selectedCareer?.id === career.id 
                            ? themeColors.primary + "15" 
                            : "transparent",
                          borderColor: selectedCareer?.id === career.id 
                            ? themeColors.primary 
                            : themeColors.border,
                          borderWidth: selectedCareer?.id === career.id ? "2px" : "1px",
                          color: themeColors.foreground
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="text-2xl">{career.icon}</div>
                          <div>
                            <p className="font-medium text-sm mb-1"
                              style={{ color: themeColors.foreground }}
                            >
                              {career.title}
                            </p>
                            <p className="text-xs opacity-70"
                              style={{ color: themeColors.foreground }}
                            >
                              {career.description}
                            </p>
                          </div>
                        </div>
                        {selectedCareer?.id === career.id && (
                          <div className="mt-3 pt-3 border-t flex items-center justify-between"
                            style={{ borderColor: themeColors.border }}
                          >
                            <span className="text-xs font-medium"
                              style={{ color: themeColors.primary }}
                            >
                              Selected
                            </span>
                            <div className="w-6 h-6 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: themeColors.primary }}
                            >
                              <ChevronRight size={14} className="text-white" />
                            </div>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button
                  onClick={handleSubmit}
                  disabled={!selectedCareer}
                  className={`w-full py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2 ${
                    selectedCareer ? "hover:scale-[1.02]" : "opacity-50 cursor-not-allowed"
                  }`}
                  style={{ 
                    backgroundColor: selectedCareer ? themeColors.primary : themeColors.primary + "50",
                    color: themeColors.cardBackground
                  }}
                >
                  Explore Curriculum
                  <ChevronRight size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Progress dots */}
          <div className="px-8 pb-6 flex items-center justify-center gap-2">
            {[1, 2].map((step) => (
              <div
                key={step}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentStep === step || (currentStep === 1.5 && step === 1) 
                    ? "scale-125" 
                    : "opacity-30"
                }`}
                style={{ 
                  backgroundColor: currentStep === step || (currentStep === 1.5 && step === 1)
                    ? themeColors.primary
                    : themeColors.foreground
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};